import Routes from '@models/routes';
import { authenticationParamsContext } from '@stores/authentication/authentication-params-context';
import { authorisationContext } from '@stores/authorisation/authorisation-context';
import { IAuthorisationStore } from '@stores/authorisation/authorisation-store';
import { observer } from 'mobx-react';
import {
    ComponentClass,
    FunctionComponent
} from 'react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import {
    Redirect,
    Route,
    RouteProps
} from 'react-router-dom';

export interface AuthorisedRouteProps extends RouteProps {
    resumable?: boolean;
}

export const renderIfAuthorised = (
    authorisationStore: IAuthorisationStore | null,
    renderAuthorised: () => React.ReactNode,
    renderUnauthorised: () => React.ReactNode
): React.ReactNode => {
    if (!authorisationStore) {
        return null;
    }

    return authorisationStore.isTokenValid ?
        renderAuthorised() :
        renderUnauthorised();
};

/**
 * Workaround as <Observer> does not accept React.ReactNode in its render callback
 * due to the presence of undefined in the union
 */
@observer
export class RouteWithAuthorisation extends React.Component<{
    routeProps: AuthorisedRouteProps;
    routeComponentProps: RouteComponentProps
}> {
    static contextType = authorisationContext;
    declare context: React.ContextType<typeof authorisationContext>;

    render() {
        return renderIfAuthorised(
            this.context,
            () => this.renderRoute(this.props.routeProps, this.props.routeComponentProps),
            () => this.renderUnauthorised()
        );
    }

    renderUnauthorised = () => {
        const { resumable } = this.props.routeProps;
        const { location } = this.props.routeComponentProps;

        return (
            <authenticationParamsContext.Consumer>
                {
                    paramsStore => (
                        <Redirect to={{
                            pathname: Routes.Authorisation,
                            search: paramsStore?.serialisedParams,
                            state: resumable === true ? { from: location } : null
                        }}/>
                    )
                }
            </authenticationParamsContext.Consumer>
        );
    };

    renderRoute = (
        { children, component, render }: AuthorisedRouteProps,
        routeComponentProps: RouteComponentProps
    ) => {
        if (typeof children === 'function') {
            return children(routeComponentProps);
        }

        if (React.isValidElement(children)) {
            return children;
        }

        if (component) {
            return React.createElement(
                component as ComponentClass<RouteComponentProps> | FunctionComponent<RouteComponentProps>,
                routeComponentProps
            );
        }

        if (render) {
            return render(routeComponentProps);
        }

        return null;
    };
}

export const AuthorisedRoute = (routeProps: AuthorisedRouteProps) => {
    // Excludes competing props for the route so only our supplied render function is used
    const {
        render,
        component,
        children,
        ...propsForParentRoute
    } = routeProps;

    return (
        <Route {...propsForParentRoute} render={(props: RouteComponentProps<any>) => (
            <RouteWithAuthorisation routeProps={routeProps}
                                    routeComponentProps={props}/>
        )}/>
    );
};

export default AuthorisedRoute;



// WEBPACK FOOTER //
// ./src/containers/authorised-route/index.tsx